import { Check, Loader2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import {
  formatCurrency,
  getRoomTypeLabel,
  Quote,
  QuoteRoom,
  QuoteStatus,
} from '../types/quote';
import { RENTAL_LENGTH_OPTIONS } from '../types/orderItem';

const QuotePublicView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [responseSubmitted, setResponseSubmitted] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  useEffect(() => {
    loadQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadQuote = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/quotes/public/${token}`);
      const data = await response.json();
      if (data.success) {
        setQuote(data.data);
      } else {
        setError(data.message || 'Quote not found');
      }
    } catch {
      setError('Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!token) return;
    try {
      setSubmitting(true);
      const response = await fetch(`/api/quotes/public/${token}/accept`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        setResponseSubmitted(true);
        setQuote((prev) => prev ? { ...prev, status: QuoteStatus.ACCEPTED } : null);
      } else {
        setError(data.message || 'Failed to accept quote');
      }
    } catch {
      setError('Failed to accept quote');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!token) return;
    try {
      setSubmitting(true);
      const response = await fetch(`/api/quotes/public/${token}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectionReason }),
      });
      const data = await response.json();
      if (data.success) {
        setResponseSubmitted(true);
        setQuote((prev) => prev ? { ...prev, status: QuoteStatus.REJECTED, rejection_reason: rejectionReason } : null);
      } else {
        setError(data.message || 'Failed to reject quote');
      }
    } catch {
      setError('Failed to reject quote');
    } finally {
      setSubmitting(false);
    }
  };

  const monthlyTotal = quote?.rooms?.reduce((sum, room) => sum + (room.items_total || 0), 0) || 0;
  const rentalMonths = quote?.rental_length_months || 6;
  const totalPrice = monthlyTotal * rentalMonths;
  const getRentalLabel = (months: number) =>
    RENTAL_LENGTH_OPTIONS.find((opt) => opt.value === months)?.label || `${months} months`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="py-12 text-center">
            <p className="text-destructive mb-4">{error || 'Quote not found'}</p>
            <p className="text-sm text-muted-foreground">
              This quote may have expired or the link is invalid.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isRespondable = quote.status === QuoteStatus.SENT || quote.status === QuoteStatus.VIEWED;
  const hasResponded = quote.status === QuoteStatus.ACCEPTED || quote.status === QuoteStatus.REJECTED;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">Homebound</h1>
              <p className="text-sm text-muted-foreground">Staging Quote</p>
            </div>
            <Badge
              variant={
                quote.status === QuoteStatus.ACCEPTED ? 'default' :
                quote.status === QuoteStatus.REJECTED ? 'destructive' :
                'secondary'
              }
            >
              {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
            </Badge>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Quote Info */}
        <div className="text-center mb-8">
          <p className="text-sm text-muted-foreground mb-2">Quote #{quote.quote_number}</p>
          <h2 className="text-3xl font-bold mb-2">{quote.property_name || 'Property Staging'}</h2>
          {quote.property_address && (
            <p className="text-muted-foreground">{quote.property_address}</p>
          )}
          {quote.valid_until && (
            <p className="text-sm text-muted-foreground mt-2">
              Valid until: {new Date(quote.valid_until).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          )}
        </div>

        {/* Response Success Message */}
        {responseSubmitted && (
          <Card className="mb-8 border-green-200 bg-green-50">
            <CardContent className="py-6 text-center">
              <Check className="h-12 w-12 mx-auto mb-4 text-green-600" />
              <p className="text-lg font-medium text-green-800">
                {quote.status === QuoteStatus.ACCEPTED
                  ? 'Thank you! Your quote has been accepted.'
                  : 'Your response has been recorded.'}
              </p>
              <p className="text-sm text-green-700 mt-2">
                {quote.status === QuoteStatus.ACCEPTED
                  ? 'Our team will be in touch shortly to arrange the next steps.'
                  : 'We appreciate your feedback.'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Room Cards with Renders */}
        <div className="space-y-6 mb-8">
          {quote.rooms?.map((room: QuoteRoom) => (
            <Card key={room.id} className="overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2">
                {/* Room Render */}
                <div className="aspect-video bg-slate-100">
                  {room.selected_render?.image_url || (room.renders && room.renders[0]?.image_url) ? (
                    <img
                      src={room.selected_render?.image_url || room.renders?.[0]?.image_url}
                      alt={`${getRoomTypeLabel(room.room_type)} render`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <p>Render pending</p>
                    </div>
                  )}
                </div>

                {/* Room Details */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">
                    {room.room_name || getRoomTypeLabel(room.room_type)}
                  </h3>
                  {room.width_ft && room.length_ft && (
                    <p className="text-sm text-muted-foreground mb-4">
                      {room.width_ft} × {room.length_ft} ft
                    </p>
                  )}

                  {/* Items */}
                  {room.items && room.items.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {room.items.map((item) => (
                        <div key={item.id} className="flex justify-between text-sm">
                          <span>
                            {item.quantity}× {item.asset_template?.name}
                          </span>
                          <span className="text-muted-foreground">
                            {formatCurrency(item.total_price)}/mo
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <Separator className="my-4" />

                  <div className="flex justify-between font-medium">
                    <span>Room Subtotal</span>
                    <span>{formatCurrency(room.items_total || 0)}/mo</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Quote Total */}
        <Card className="mb-8">
          <CardContent className="py-6">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Monthly Rate</span>
                <span className="font-medium">{formatCurrency(monthlyTotal)}/mo</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Rental Duration</span>
                <span className="font-medium">{getRentalLabel(rentalMonths)}</span>
              </div>
              <Separator />
              <div className="flex justify-between items-center text-xl font-bold">
                <span>Total Investment</span>
                <span>{formatCurrency(totalPrice)}</span>
              </div>
            </div>
            {quote.notes && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">{quote.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Response Actions */}
        {isRespondable && !responseSubmitted && (
          <Card>
            <CardHeader>
              <CardTitle>Ready to proceed?</CardTitle>
              <CardDescription>
                Accept this quote to confirm your staging order, or let us know if you have any concerns.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {showRejectForm ? (
                <div className="space-y-4">
                  <Textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please let us know your concerns or what we could improve..."
                    rows={3}
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      onClick={handleReject}
                      disabled={submitting}
                      className="flex-1"
                    >
                      {submitting ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <X className="h-4 w-4 mr-2" />
                      )}
                      Submit Feedback
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowRejectForm(false)}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-4">
                  <Button
                    onClick={handleAccept}
                    disabled={submitting}
                    className="flex-1"
                    size="lg"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Accept Quote
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowRejectForm(true)}
                    disabled={submitting}
                    size="lg"
                  >
                    Request Changes
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Already Responded */}
        {hasResponded && !responseSubmitted && (
          <Card>
            <CardContent className="py-6 text-center">
              <p className="text-muted-foreground">
                {quote.status === QuoteStatus.ACCEPTED
                  ? 'This quote has been accepted. Our team will be in touch shortly.'
                  : 'This quote has been declined.'}
              </p>
              {quote.rejection_reason && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Feedback: "{quote.rejection_reason}"
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t text-center text-sm text-muted-foreground">
          <p>Questions? Contact us at support@homebound.com</p>
          <p className="mt-2">© {new Date().getFullYear()} Homebound. All rights reserved.</p>
        </footer>
      </main>
    </div>
  );
};

export default QuotePublicView;
